// Safety Overview modes (Yaron, 2026-10-22). The page opens on a start screen: pick the data
// (RDV analyses), then a mode. "Full capabilities" (the RDV button) is the Safety Dashboard with
// every data set and setting; the server opens it to RDV and to the customers flagged for it
// (view_safety_overview_full, config/permission_grants.json), on their own projects. The customer modes are the same parts, simplified: three filters,
// the deficiency sites on a clean map, the Top 3 per analysis, two panels. Each customer mode
// gets its own content later; for now they share this one definition.
export const FULL = 'full';

export const MODES = [
  { key: 'maintenance', label: 'Maintenance', short: 'Maintenance', icon: 'fa-screwdriver-wrench',
    blurb: 'Vegetation trimming and roadside work priorities on the existing road.' },
  { key: 'rehab', label: 'Existing projects / rehab', short: 'Existing / rehab', icon: 'fa-road',
    blurb: 'Every deficiency on a project segment: what else to address while the crews are out there.' },
  { key: 'newProject', label: 'New project spawning', short: 'New project', icon: 'fa-compass-drafting',
    blurb: 'The deficiency hotspots that should become future projects.' },
  { key: FULL, label: 'Full capabilities', short: 'RDV', rdv: true,
    blurb: 'Everything the Safety Dashboard has: crash data, road features, clusters, insights, the program, the PDF.' },
];
export const modeOf = (key) => MODES.find((m) => m.key === key) || null;
export const isFull = (key) => key === FULL;

/** What a mode starts with. Full keeps the page's own defaults; the customer modes start on the Light gray map.
 *  Maintenance: the Top 3 per analysis, severe. Rehab and New project spawning (meeting 2026-10-26): comprehensive,
 *  every severity and every site (the Top N stays a choice); New project shows the Top 5 hotspots. */
const EVERY_SEVERITY = ['severe', 'moderate', 'minor'];
export const modeDefaults = (key) => {
  if (isFull(key)) return { baseLayer: 'street', mapTop: 0, rankBy: 'combined' };
  const base = { baseLayer: 'lightgray', mapTop: 3, rankBy: 'analysis', rows: 3, severities: ['severe'] };
  const comprehensive = { ...base, mapTop: 0, rows: 0, severities: EVERY_SEVERITY };
  if (key === 'rehab') return comprehensive;
  return key === 'newProject' ? { ...comprehensive, hotspotsTop: 5 } : base;
};

const SEVERITY_ORDER = { severe: 3, moderate: 2, minor: 1 };
const CURVE_OVERLAP = 0.3;      // the two directions' curves are one curve when they share this part of the shorter one
/** One horizontal curve surveyed both ways is one curve (meeting 2026-10-26): the P and N sites of a curve join into
 *  one row, carrying the worse direction's value, severity and report, and each direction under `members`. Twin of
 *  overview_data.merge_curve_directions: the id (the smallest of its members') is what the PDF is asked for. */
export function mergeCurveDirections(sites) {
  const curves = sites.filter((s) => s.analysis === 'HCA' && s.mpStart != null);
  const parent = new Map(curves.map((s) => [s.id, s.id]));
  const find = (id) => { let x = id; while (parent.get(x) !== x) x = parent.get(x); return x; };
  curves.forEach((a, i) => curves.slice(i + 1).forEach((b) => {
    if (a.segKey !== b.segKey || a.direction === b.direction) return;
    const aEnd = a.mpEnd ?? a.mpStart;
    const bEnd = b.mpEnd ?? b.mpStart;
    const shared = Math.min(aEnd, bEnd) - Math.max(a.mpStart, b.mpStart);
    if (shared > 0 && shared >= CURVE_OVERLAP * Math.min(aEnd - a.mpStart, bEnd - b.mpStart)) parent.set(find(a.id), find(b.id));
  }));
  const groups = new Map();
  curves.forEach((s) => { const k = find(s.id); groups.set(k, [...(groups.get(k) || []), s]); });
  const replaced = new Map();
  groups.forEach((members) => {
    if (members.length < 2) return;
    const worst = [...members].sort((a, b) => (SEVERITY_ORDER[b.severity] || 0) - (SEVERITY_ORDER[a.severity] || 0)
      || (a.worstValue ?? 0) - (b.worstValue ?? 0) || (a.id < b.id ? -1 : 1))[0];
    const ids = members.map((m) => m.id).sort();
    const mpStart = Math.min(...members.map((m) => m.mpStart));
    const mpEnd = Math.max(...members.map((m) => m.mpEnd ?? m.mpStart));
    const merged = { ...worst, id: ids[0], mpStart, mpEnd, lengthMi: mpEnd - mpStart, directions: ['P', 'N'],
      members: members.map((m) => ({ direction: m.direction, mpStart: m.mpStart, mpEnd: m.mpEnd, severity: m.severity, worstText: m.worstText, reportUrl: m.reportUrl })) };
    members.forEach((m) => replaced.set(m.id, ids[0] === m.id ? merged : null));
  });
  return sites.filter((s) => replaced.get(s.id) !== null).map((s) => replaced.get(s.id) || s);
}

// ---- Maintenance: the work the user looks at (Yaron, 2026-10-22). Crews trim, clean and dig; they do not change a
// road's alignment, so vertical curvature and horizontal curves belong to rehab (meeting 2026-10-26)
export const MAINT_TASKS = [
  { key: 'vegetation', label: 'Vegetation trimming', icon: 'fa-leaf',
    blurb: 'Sight-distance deficiencies blocked by vegetation: where trimming restores the view.' },
  // a flat spot is never graded severe: this work opens on what it has
  { key: 'flat', label: 'Flat spots', icon: 'fa-droplet', severities: ['moderate'],
    blurb: 'Where water can stand on the pavement: drainage to clean out, plowing to watch.' },
  { key: 'roadside', label: 'Roadside terrain', icon: 'fa-person-digging',
    blurb: 'Sight-distance deficiencies caused by terrain beside the road.' },
];
/** The severities a Maintenance work opens on. */
export const maintenanceSeverities = (task) => (MAINT_TASKS.find((t) => t.key === task) || {}).severities || modeDefaults('maintenance').severities;
/** Roadside work's items, each switched on and off by the user. */
export const ROADSIDE_ITEMS = [
  { key: 'terrain', kind: 'obstruction', label: 'Terrain (SSD, ISD)' },
];
const SIGHT = ['SSD', 'ISD'];
/** The analyses a Maintenance task allows and the obstruction rule it applies. */
export function maintenanceScope(task, items) {
  if (task === 'vegetation') return { analyses: SIGHT, obstructions: ['vegetation'], sightOnly: true };
  if (task === 'flat') return { analyses: ['FSA'], obstructions: [], sightOnly: true };
  if (task === 'roadside') {
    const obstructions = ROADSIDE_ITEMS.filter((i) => (items || {})[i.key] !== false).map((i) => i.key);
    return { analyses: obstructions.length ? SIGHT : [], obstructions: obstructions.length ? obstructions : ['__none__'], sightOnly: true };
  }
  return null;
}

// ---- New project spawning: deficiency hotspots
// find by: 'cluster' (the hotspot rule) or 'length' (the best stretches of a project length, e.g. what the budget covers)
export const HOTSPOT_DEFAULTS = { gapMi: 0.25, minSites: 3, byDirection: false, findBy: 'cluster', lengthMi: 1.5 };

/** Stretches where deficiency sites follow each other closely: per segment (and per direction when
 *  byDirection), sites whose gaps are at most gapMi join one hotspot; at least minSites make one.
 *  Scored by the sum of their sites' scores, worst first. */
export function buildHotspots(sites, { gapMi, minSites, byDirection }, scoreOf) {
  const groups = {};
  sites.filter((s) => s.mpStart != null).forEach((s) => {
    const k = `${s.segKey}|${byDirection ? s.direction : '*'}`;
    (groups[k] = groups[k] || []).push(s);
  });
  const out = [];
  Object.entries(groups).forEach(([k, list]) => {
    const sorted = [...list].sort((a, b) => a.mpStart - b.mpStart);
    let cur = null;
    const close = () => { if (cur && cur.sites.length >= minSites) out.push(cur); };
    sorted.forEach((s) => {
      const end = s.mpEnd ?? s.mpStart;
      if (cur && s.mpStart <= cur.mpEnd + gapMi) {
        cur.sites.push(s);
        cur.mpEnd = Math.max(cur.mpEnd, end);
      } else {
        close();
        cur = { key: k, segKey: s.segKey, direction: byDirection ? s.direction : null, mpStart: s.mpStart, mpEnd: end, sites: [s] };
      }
    });
    close();
  });
  return out.map((h) => {
    const analyses = Array.from(new Set(h.sites.map((s) => s.analysis)));
    const lengthMi = Math.max(0, h.mpEnd - h.mpStart);
    const mid = h.sites[Math.floor(h.sites.length / 2)];
    return {
      ...h, id: `hs:${h.key}|${h.mpStart}`, analyses, lengthMi, n: h.sites.length,
      severe: h.sites.filter((s) => s.severity === 'severe').length,
      perMi: h.sites.length / Math.max(lengthMi, 0.1),
      score: h.sites.reduce((t, s) => t + scoreOf(s), 0),
      lat: mid.lat, lng: mid.lng, road: mid.road, segment: mid.segment,
      directions: Array.from(new Set(h.sites.flatMap((s) => s.directions || [s.direction]))).sort(),
      parts: h.sites.map((s) => ({ site: s, share: 1 })),
    };
  }).sort((a, b) => b.score - a.score || b.n - a.n || a.mpStart - b.mpStart);
}

const LAST_MODE_KEY = 'safetyOverview:lastMode';
const RANK_BY_KEY = 'safetyOverview:rankBy';
const read = (k, d) => { try { const v = localStorage.getItem(k); return v == null ? d : JSON.parse(v); } catch (e) { return d; } };
const write = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) { /* private window */ } };

export const recallMode = () => read(LAST_MODE_KEY, null);
/** A per-user choice of the customer modes (the Maintenance task, roadside items, hotspot settings). */
export const loadPref = (name, d) => { const v = read(`safetyOverview:${name}`, null); return v == null ? d : v; };
export const savePref = (name, v) => write(`safetyOverview:${name}`, v);
export const rememberMode = (key) => write(LAST_MODE_KEY, key);

/** The user's rank flag, kept per mode: 'analysis' (each analysis ranked on its own) or 'combined'. */
export const loadRankBy = (key) => {
  const v = (read(RANK_BY_KEY, {}) || {})[key];
  return v === 'analysis' || v === 'combined' ? v : modeDefaults(key).rankBy;
};
export const saveRankBy = (key, v) => write(RANK_BY_KEY, { ...(read(RANK_BY_KEY, {}) || {}), [key]: v });

/** The map's Top N, kept per mode (Full keeps the key it always had). */
export const mapTopKey = (key) => (isFull(key) ? 'safetyOverview:mapTop' : `safetyOverview:mapTop:${key}`);

/** Ranks within each analysis. `ranked` is the combined rank order (best first); returns
 *  {rank: id -> n within its analysis, order: id -> position when grouped by analysis
 *  (analyses in `analyses` order, each best first), byAnalysis: analysis -> [sites]}. */
export function rankWithinAnalysis(ranked, analyses) {
  const byAnalysis = {};
  ranked.forEach((s) => { (byAnalysis[s.analysis] = byAnalysis[s.analysis] || []).push(s); });
  const rank = new Map();
  const order = new Map();
  let i = 0;
  [...analyses, ...Object.keys(byAnalysis).filter((a) => !analyses.includes(a))].forEach((a) => {
    (byAnalysis[a] || []).forEach((s, k) => { rank.set(s.id, k + 1); order.set(s.id, (i += 1)); });
  });
  return { rank, order, byAnalysis };
}

/** The map's sites: the top n of each analysis (n = 0: all), and the pinned ones too. */
export function topPerAnalysis(byAnalysis, n, pinnedIds) {
  const out = [];
  const ids = new Set();
  Object.values(byAnalysis).forEach((list) => (n ? list.slice(0, n) : list).forEach((s) => { out.push(s); ids.add(s.id); }));
  Object.values(byAnalysis).flat().forEach((s) => { if (pinnedIds.includes(s.id) && !ids.has(s.id)) out.push(s); });
  return out;
}

/** Project prospects (Yaron, 2026-10-25): the best stretches of exactly lengthMi along each segment (and
 *  direction when byDirection). A window's score is what lies INSIDE it: a site counts by the share of its
 *  length inside the window (a point site fully when inside). The best window is taken, then the next best
 *  that does not overlap a taken one on the same road, and so on. extentOf(segKey) -> [mpMin, mpMax] keeps a
 *  window on its road (the milepost range narrows it). */
export function buildProspects(sites, { lengthMi, byDirection }, scoreOf, extentOf) {
  const L = Math.max(0.05, Number(lengthMi) || 0);
  const groups = {};
  sites.filter((s) => s.mpStart != null).forEach((s) => {
    const k = `${s.segKey}|${byDirection ? s.direction : '*'}`;
    (groups[k] = groups[k] || []).push(s);
  });
  const share = (s, a, b) => {
    const lo = s.mpStart;
    const hi = s.mpEnd ?? s.mpStart;
    if (hi - lo < 1e-6) return lo >= a - 1e-9 && lo <= b + 1e-9 ? 1 : 0;
    return Math.max(0, Math.min(hi, b) - Math.max(lo, a)) / (hi - lo);
  };
  const candidates = [];
  Object.entries(groups).forEach(([k, list]) => {
    const [segKey] = k.split('|');
    const ext = extentOf(segKey) || [Math.min(...list.map((s) => s.mpStart)), Math.max(...list.map((s) => s.mpEnd ?? s.mpStart))];
    const span = Math.min(L, Math.max(0, ext[1] - ext[0]));
    const fit = (a) => Math.min(Math.max(a, ext[0]), ext[1] - span);
    // windows that start at a site's start or end at a site's end are the only ones that can be best
    const starts = new Set();
    list.forEach((s) => { starts.add(fit(s.mpStart)); starts.add(fit((s.mpEnd ?? s.mpStart) - span)); });
    starts.forEach((a) => {
      const b = a + span;
      const inside = list.map((s) => [s, share(s, a, b)]).filter(([, f]) => f > 1e-9);
      if (!inside.length) return;
      candidates.push({ key: k, segKey, direction: byDirection ? list[0].direction : null, mpStart: a, mpEnd: b, inside,
        score: inside.reduce((t, [s, f]) => t + scoreOf(s) * f, 0) });
    });
  });
  candidates.sort((x, y) => y.score - x.score || x.mpStart - y.mpStart);
  const taken = [];
  for (const c of candidates) {
    if (taken.some((t) => t.key === c.key && c.mpStart < t.mpEnd - 1e-9 && c.mpEnd > t.mpStart + 1e-9)) continue;
    taken.push(c);
  }
  return taken.map((c) => {
    const siteList = c.inside.map(([s]) => s);
    const mid = siteList[Math.floor(siteList.length / 2)];
    const lengthMiW = c.mpEnd - c.mpStart;
    return {
      key: c.key, segKey: c.segKey, direction: c.direction, mpStart: c.mpStart, mpEnd: c.mpEnd, sites: siteList,
      id: `pp:${c.key}|${c.mpStart.toFixed(4)}`, analyses: Array.from(new Set(siteList.map((s) => s.analysis))),
      lengthMi: lengthMiW, n: siteList.length, severe: siteList.filter((s) => s.severity === 'severe').length,
      perMi: siteList.length / Math.max(lengthMiW, 0.1), score: c.score, lat: mid.lat, lng: mid.lng, road: mid.road, segment: mid.segment,
      directions: Array.from(new Set(siteList.flatMap((s) => s.directions || [s.direction]))).sort(),
      // the road (or the search area) is shorter than the project length: the stretch is all there is
      short: lengthMiW < L - 1e-6, requestedMi: L,
      parts: c.inside.map(([site, share]) => ({ site, share })),
    };
  });
}

